import { callTool } from "../api";
import { resolveMapHall } from "./mapHall";
import { accountSearchHitToBoardRow, type AccountSearchResult } from "../ratings";
import type { BoothSearchResult } from "../types";

export type BoothSearchHit = {
  booth: string;
  label: string;
  hall?: string;
};

export function formatBoothHit(hit: BoothSearchHit): string {
  const name = hit.label.trim();
  if (!name || name === hit.booth) return hit.booth;
  return `${name} · ${hit.booth}`;
}

export function normalizeBoothId(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

export function looksLikeBoothNumber(query: string): boolean {
  return /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9_-]{0,11}$/.test(query.trim());
}

export function extractBoothFromDisplay(query: string): string {
  const parts = query
    .split("·")
    .map((part) => part.trim())
    .filter(Boolean);
  const last = parts[parts.length - 1];
  if (parts.length >= 2 && last && looksLikeBoothNumber(last)) return last;
  return query.trim();
}

function searchNeedle(query: string): string {
  const parts = query
    .split("·")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 2 && parts[0] && looksLikeBoothNumber(parts[parts.length - 1] ?? "")) {
    return parts[0];
  }
  return query.trim();
}

function rankHit(hit: BoothSearchHit, query: string): number {
  const q = query.trim().toLowerCase();
  const booth = hit.booth.toLowerCase();
  const label = hit.label.toLowerCase();
  if (booth === q) return 0;
  if (booth.startsWith(q)) return 1;
  if (label === q) return 2;
  if (label.startsWith(q)) return 3;
  return 4;
}

function matchQuery(hits: BoothSearchHit[], query: string): BoothSearchHit | undefined {
  const q = query.trim().toLowerCase();
  const boothKey = extractBoothFromDisplay(query).toLowerCase();
  const formatted = hits.find((hit) => formatBoothHit(hit).toLowerCase() === q);
  if (formatted) return formatted;
  const exactBooth = hits.find((hit) => hit.booth.toLowerCase() === boothKey);
  if (exactBooth && (looksLikeBoothNumber(extractBoothFromDisplay(query)) || hits.length === 1)) {
    return exactBooth;
  }
  if (hits.length === 1) return hits[0];
  return undefined;
}

export async function searchFloorBooths(
  eventKey: string,
  query: string,
  _campaignKey?: string,
  toolCaller: typeof callTool = callTool,
): Promise<BoothSearchHit[]> {
  const needle = searchNeedle(query);
  if (!needle) return [];

  const [booths, accounts] = await Promise.all([
    toolCaller<BoothSearchResult>("booth_search", { event_key: eventKey, query: needle, limit: 12 }).catch(
      (): BoothSearchResult => ({ results: [] }),
    ),
    toolCaller<AccountSearchResult>("account_search", {
      event_key: eventKey,
      name: needle,
      query: needle,
      limit: 12,
    }).catch((): AccountSearchResult => ({ results: [] })),
  ]);

  const byBooth = new Map<string, BoothSearchHit>();

  for (const booth of booths.results ?? []) {
    const id = (booth.booth_number || booth.booth_id || "").trim();
    if (!id) continue;
    byBooth.set(id, {
      booth: id,
      label: (booth.company_name ?? "").trim() || id,
      hall: resolveMapHall(booth.hall_name, id),
    });
  }

  for (const hit of accounts.results ?? []) {
    const row = accountSearchHitToBoardRow(hit);
    const id = (row.booth ?? "").trim();
    if (!id) continue;
    const existing = byBooth.get(id);
    byBooth.set(id, {
      booth: id,
      label: (row.name ?? "").trim() || existing?.label || id,
      hall: resolveMapHall(row.hall, id) ?? existing?.hall,
    });
  }

  return [...byBooth.values()]
    .sort((a, b) => {
      const rank = rankHit(a, needle) - rankHit(b, needle);
      if (rank !== 0) return rank;
      return a.label.localeCompare(b.label);
    })
    .slice(0, 12);
}

export async function resolveFloorBooth(
  eventKey: string,
  query: string,
  campaignKey?: string,
  toolCaller: typeof callTool = callTool,
): Promise<BoothSearchHit | null> {
  const raw = query.trim();
  if (!raw) return null;
  const hits = await searchFloorBooths(eventKey, raw, campaignKey, toolCaller);
  const matched = matchQuery(hits, raw);
  if (matched) return matched;
  const boothOnly = extractBoothFromDisplay(raw);
  if (looksLikeBoothNumber(boothOnly)) {
    return hits.find((hit) => hit.booth.toLowerCase() === boothOnly.toLowerCase()) ?? { booth: boothOnly, label: boothOnly };
  }
  return hits[0] ?? null;
}
